import * as cheerio from 'cheerio';

// Utility function to retrieve the nonce value from the DOM
function getNonce(): string | null {
  const element = document.querySelector('clients-root');
  return element ? element.getAttribute('ngcspnonce') : null;
}

// Generalized method to add the nonce to any inline style attributes in the HTML
export function replaceInlineStyleNonce(html: string): string {
  const nonce = getNonce();

  // If no nonce is found, return the original HTML (no modification)
  if (!nonce) {
    return html;
  }

  return html.replace(/<style(\s[^>]*)?>/g, (match) => {
    // If style tag already has a nonce, skip it
    if (/nonce\s*=/.test(match)) return match;
    // Inject nonce
    return match.replace(/<style/, `<style nonce="${nonce}"`);
  });
}

/**
 * Generates a short random string for unique class naming.
 */
function generateId(length = 6): string {
  return Math.random().toString(36).substring(2, 2 + length);
}

/**
 * Converts inline styles into CSS classes with !important and unique class names.
 * Adds a <style> block to the HTML output.
 */
export function extractInlineStylesToCssClass(html: string): string {
  if (typeof html !== 'string') {
    console.warn('extractInlineStylesToCssClass received non-string input:', html);
    return '';
  }
  
  const $ = cheerio.load(html);
  const styleMap: Map<string, string> = new Map();

  $('[style]').each((_, el) => {
    const style = $(el).attr('style')?.trim();
    if (!style) return;

    // Convert style to "!important"
    const importantStyle = style
      .split(';')
      .map(decl => decl.trim())
      .filter(Boolean)
      .map(decl => {
        const [prop, value] = decl.split(':').map(part => part.trim());
        return `${prop}: ${value} !important`;
      })
      .join('; ');

    // Check if a class already exists for this exact style
    let className = [...styleMap.entries()].find(([, s]) => s === importantStyle)?.[0];
    if (!className) {
      className = `style-${generateId()}`;
      styleMap.set(className, importantStyle);
    }

    $(el).removeAttr('style').addClass(className);
  });

  if (styleMap.size === 0) {
    return $.html(); // No styles, return original HTML
  }

  // Build CSS string
  const cssText = Array.from(styleMap.entries())
    .map(([className, style]) => `.${className} { ${style}; }`)
    .join('\n');

  const styleTag = `<style>${cssText}</style>`;
  return styleTag + $.html();
}
